type FileType = {
  signature: number[];
  type: string;
}

/**
 * Supported file types can be easily extended across the platform by adding entries here.
 * This will apply out of the box everywhere.
 *
 * @example
 * jpg: {
 *   signature: [0xFF, 0xD8, 0xFF],
 *   type: 'image/jpeg',
 * },
 * png: {
 *   signature: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],
 *   type: 'image/png',
 * },
 *
 * @see https://en.wikipedia.org/wiki/List_of_file_signatures
 * @see https://www.iana.org/assignments/media-types/media-types.xhtml
 */
const FileTypes: Record<string, FileType> = Object.freeze({
  pdf: {
    signature: [0x25, 0x50, 0x44, 0x46, 0x2D],
    type: 'application/pdf',
  },
});

const FileHelpers = {
  handleFile(arrayBuffer: Uint8Array, filename: string) {
    new Blob([arrayBuffer])
      .slice(0, Math.max(...Object.values(FileTypes)
        .map(({ signature }) => signature.length)))
      .arrayBuffer()
      .then((headerBuffer) => {
        const blob = new Blob([arrayBuffer]);
        const header = new Uint8Array(headerBuffer);

        const found = Object.values(FileTypes)
          .find(({ signature }) => signature.every((byte, index) => header[index] === byte));

        if (found) {
          FileHelpers.openFile(URL.createObjectURL(new Blob([arrayBuffer], { type: found.type })));
        } else {
          FileHelpers.downloadFile(URL.createObjectURL(blob), filename);
        }
      });
  },
  openFile(url: string) {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  downloadFile(url: string, filename: string) {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  base64ToArrayBuffer(base64: string) {
    const binaryString = window.atob(base64);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  },
  formatBits(bits: number) {
    const oneMB = 1024 * 1024;
    if (bits < oneMB) return `${(bits / 1024).toFixed(1)} kB`;
    return `${(bits / oneMB).toFixed(1)} MB`;
  },
};

export default FileHelpers;
